"use client"

import type React from "react"

import { useRef, useEffect, useCallback, useState, memo } from "react"
import type { Enemy } from "@/types/enemy.types"
import type { Tower } from "@/types/tower.types"
import type { Hero } from "@/types/hero.types"
import type { LevelConfig, Position, ViewportState } from "@/types/game.types"
import { gameSettings } from "@/data/game-settings"

interface MinimapSectionProps {
  enemies: Enemy[]
  towers: Tower[]
  hero: Hero | null
  level: LevelConfig
  viewport: ViewportState
  onViewportChange: (position: Position) => void
  onZoomIn: () => void
  onZoomOut: () => void
  isZoomedOut: boolean
  onDeselectHero?: () => void
  scale: number
  isDroppodSelected?: boolean
}

export const MinimapSection = memo(function MinimapSection({
  enemies,
  towers,
  hero,
  level,
  viewport,
  onViewportChange,
  onZoomIn,
  onZoomOut,
  isZoomedOut,
  onDeselectHero,
  scale,
  isDroppodSelected = false,
}: MinimapSectionProps) {
  const minimapRef = useRef<HTMLCanvasElement>(null)
  const lastTapRef = useRef<number>(0)
  const pinchStartRef = useRef<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Based on 512x152: minimap area is roughly 430-505px left, 16-136px top
  const areaWidth = 75 * scale
  const areaHeight = 120 * scale
  const left = 420 * scale
  const top = 16 * scale

  // Calculate minimap size to fit in the scroll area while maintaining aspect ratio
  const levelWidth = gameSettings.canvas.width
  const levelHeight = gameSettings.canvas.height
  const levelAspect = levelWidth / levelHeight

  let minimapW: number
  let minimapH: number
  if (levelAspect > areaWidth / areaHeight) {
    minimapW = areaWidth * 0.9
    minimapH = minimapW / levelAspect
  } else {
    minimapH = areaHeight * 0.9
    minimapW = minimapH * levelAspect
  }

  // Draw minimap
  useEffect(() => {
    const canvas = minimapRef.current
    if (!canvas || minimapW === 0 || minimapH === 0) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const scaleX = minimapW / levelWidth
    const scaleY = minimapH / levelHeight

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Blue background when droppod is selected, tan otherwise
    ctx.fillStyle = isDroppodSelected ? "rgba(60, 100, 140, 0.9)" : "rgba(210, 180, 140, 0.8)"
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Draw path
    ctx.strokeStyle = "#ffffff"
    ctx.lineWidth = Math.max(2, level.pathWidth * scaleX * 0.5)
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.beginPath()
    for (let i = 0; i < level.path.length; i++) {
      const point = level.path[i]
      const x = point.x * scaleX
      const y = point.y * scaleY
      if (i === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()

    // Draw start line (green)
    if (level.path.length >= 2) {
      const start = level.path[0]
      ctx.strokeStyle = "#22c55e"
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.moveTo(start.x * scaleX - 8, start.y * scaleY)
      ctx.lineTo(start.x * scaleX + 8, start.y * scaleY)
      ctx.stroke()
    }

    // Draw finish line (red)
    ctx.strokeStyle = "#ef4444"
    ctx.lineWidth = 3
    ctx.beginPath()
    const baseX = level.basePosition.x * scaleX
    const baseY = level.basePosition.y * scaleY
    ctx.moveTo(baseX - 8, baseY)
    ctx.lineTo(baseX + 8, baseY)
    ctx.stroke()

    // Draw towers
    for (const tower of towers) {
      const brightness = 0.3 + (tower.level / 3) * 0.7
      const blue = Math.floor(100 + brightness * 155)
      ctx.fillStyle = `rgb(60, 130, ${blue})`
      ctx.beginPath()
      ctx.arc(tower.position.x * scaleX, tower.position.y * scaleY, 3, 0, Math.PI * 2)
      ctx.fill()
    }

    // Draw enemies
    ctx.fillStyle = "#ef4444"
    for (const enemy of enemies) {
      ctx.beginPath()
      ctx.arc(enemy.position.x * scaleX, enemy.position.y * scaleY, 2, 0, Math.PI * 2)
      ctx.fill()
    }

    // Draw hero
    if (hero) {
      ctx.fillStyle = "#22c55e"
      ctx.beginPath()
      ctx.arc(hero.position.x * scaleX, hero.position.y * scaleY, 4, 0, Math.PI * 2)
      ctx.fill()
    }

    // Draw viewport box
    ctx.strokeStyle = "#000000"
    ctx.lineWidth = 1.5
    const viewWidth = viewport.width / viewport.zoom
    const viewHeight = viewport.height / viewport.zoom
    ctx.strokeRect(viewport.x * scaleX, viewport.y * scaleY, viewWidth * scaleX, viewHeight * scaleY)
  }, [enemies, towers, hero, level, viewport, minimapW, minimapH, levelWidth, levelHeight, isDroppodSelected])

  const handleMinimapInteraction = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = minimapRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const x = clientX - rect.left
      const y = clientY - rect.top

      const scaleX = minimapW / levelWidth
      const scaleY = minimapH / levelHeight

      const viewWidth = viewport.width / viewport.zoom
      const viewHeight = viewport.height / viewport.zoom
      const levelX = x / scaleX - viewWidth / 2
      const levelY = y / scaleY - viewHeight / 2

      const clampedX = Math.max(0, Math.min(levelWidth - viewWidth, levelX))
      const clampedY = Math.max(0, Math.min(levelHeight - viewHeight, levelY))

      onViewportChange({ x: clampedX, y: clampedY })
    },
    [minimapW, minimapH, viewport.width, viewport.height, viewport.zoom, onViewportChange, levelWidth, levelHeight],
  )

  const handleMinimapMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = minimapRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      const scaleX = minimapW / levelWidth
      const scaleY = minimapH / levelHeight

      const viewWidth = viewport.width / viewport.zoom
      const viewHeight = viewport.height / viewport.zoom
      const boxX = viewport.x * scaleX
      const boxY = viewport.y * scaleY
      const boxW = viewWidth * scaleX
      const boxH = viewHeight * scaleY

      if (x >= boxX && x <= boxX + boxW && y >= boxY && y <= boxY + boxH) {
        setIsDragging(true)
        e.preventDefault()
      } else {
        handleMinimapInteraction(e.clientX, e.clientY)
      }
    },
    [minimapW, minimapH, viewport, handleMinimapInteraction, levelWidth, levelHeight],
  )

  const handleMinimapMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDragging) return
      handleMinimapInteraction(e.clientX, e.clientY)
    },
    [isDragging, handleMinimapInteraction],
  )

  const handleMinimapMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMinimapMouseLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMinimapClick = useCallback(
    (_e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isDragging) return

      onDeselectHero?.()

      const now = Date.now()
      const timeSinceLastTap = now - lastTapRef.current

      if (timeSinceLastTap < 300) {
        if (isZoomedOut) {
          onZoomIn()
        } else {
          onZoomOut()
        }
        lastTapRef.current = 0
      } else {
        lastTapRef.current = now
      }
    },
    [isDragging, isZoomedOut, onZoomIn, onZoomOut, onDeselectHero],
  )

  const handleMinimapTouch = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault()

      onDeselectHero?.()

      if (e.touches.length === 2) {
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY)
        pinchStartRef.current = distance
        return
      }

      const touch = e.touches[0]
      if (touch) {
        const now = Date.now()
        const timeSinceLastTap = now - lastTapRef.current

        if (timeSinceLastTap < 300) {
          if (isZoomedOut) {
            onZoomIn()
          } else {
            onZoomOut()
          }
          lastTapRef.current = 0
        } else {
          handleMinimapInteraction(touch.clientX, touch.clientY)
          lastTapRef.current = now
        }
      }
    },
    [handleMinimapInteraction, isZoomedOut, onZoomIn, onZoomOut, onDeselectHero],
  )

  const handleMinimapTouchMove = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault()

      if (e.touches.length === 2 && pinchStartRef.current !== null) {
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY)
        const delta = distance - pinchStartRef.current

        if (Math.abs(delta) > 50) {
          if (delta > 0 && isZoomedOut) {
            onZoomIn()
          } else if (delta < 0 && !isZoomedOut) {
            onZoomOut()
          }
          pinchStartRef.current = distance
        }
        return
      }

      const touch = e.touches[0]
      if (touch) {
        handleMinimapInteraction(touch.clientX, touch.clientY)
      }
    },
    [handleMinimapInteraction, isZoomedOut, onZoomIn, onZoomOut],
  )

  const handleMinimapTouchEnd = useCallback(() => {
    pinchStartRef.current = null
  }, [])

  return (
    <div
      id="minimap-section"
      className="absolute flex items-center justify-center pointer-events-auto"
      style={{
        left,
        top,
        width: areaWidth,
        height: areaHeight,
      }}
    >
      <canvas
        ref={minimapRef}
        id="minimap-canvas"
        width={minimapW}
        height={minimapH}
        className={isDragging ? "cursor-grabbing" : "cursor-pointer"}
        style={{
          imageRendering: "pixelated",
          touchAction: "none",
        }}
        onClick={handleMinimapClick}
        onMouseDown={handleMinimapMouseDown}
        onMouseMove={handleMinimapMouseMove}
        onMouseUp={handleMinimapMouseUp}
        onMouseLeave={handleMinimapMouseLeave}
        onTouchStart={handleMinimapTouch}
        onTouchMove={handleMinimapTouchMove}
        onTouchEnd={handleMinimapTouchEnd}
      />
    </div>
  )
})
